import { ErrorResponse } from "apollo-link-error";
import React, { PureComponent, ReactNode } from "react";
import { giftsDictionary } from "src/constants/giftsDictionary";
import history from "src/lib/history";
import toaster from "src/lib/toaster";
import theme, { media } from "src/theme";
import styled from "styled-components";

import { Button } from "..";
import { ICartProductAdd } from "../../hoc/persister/cartProductAdd/cartProductAdd";
import { IUserProductAlertStockUpdate } from "../../hoc/persister/userProductAlertStockUpdate/userProductAlertStockUpdate";
import {
  pushAddToCartTracking,
  subscribeToAlertTracking,
} from "../../services/analytics.service";
import { translate } from "../../services/translation.service";
import { IProduct } from "../../types/product";
import QuantityButton from "../QuantityButton";

interface IFontSize {
  fontSize: string;
}
interface IFullWidth {
  fullWidth?: boolean;
}

const Wrapper = styled.div<IFullWidth>`
  width: 100%;

  ${media.laptop} {
    width: ${({ fullWidth }: IFullWidth): string =>
      fullWidth ? "100%" : "initial"};
    display: ${({ fullWidth }: IFullWidth): string =>
      fullWidth ? "block" : "inline-block"};
  }
`;

const ActionButton = styled(Button)<IFullWidth & IFontSize>`
  margin-top: ${theme.margins.marginBase};
  font-size: ${({ fontSize }: IFontSize): string => fontSize};
  width: ${({ fullWidth }: IFullWidth): string => (fullWidth ? "100%" : "")};
  padding: ${theme.margins.paddingMedium};
  border: 1px solid ${theme.colors.black};

  ${media.tablet} {
    padding: ${theme.margins.paddingMedium} ${theme.margins.paddingPageBig};
  }
  &:hover {
    background-color: transparent;
    color: ${theme.colors.black};
  }
`;

const StyledQuantityButton = styled(QuantityButton)<IFontSize>`
  margin-top: ${theme.margins.marginBase};
  justify-content: center;

  button {
    font-size: ${({ fontSize }: IFontSize): string => fontSize};
  }

  ${media.tablet} {
    justify-content: initial;
  }
`;

export interface IProps extends IUserProductAlertStockUpdate, ICartProductAdd {
  canBeAddedToCart?: boolean;
  canEditQuantity?: boolean;
  className?: string;
  product: IProduct;
  fullWidth?: boolean;
  children?: ReactNode | string;
  fontSize?: string | number;
  label?: string;
}

export interface IState {
  quantity: number;
}

type IDefaultProps = Required<Pick<IProps, "canEditQuantity">>;

export default class ProductAddButton extends PureComponent<
  IProps,
  IState,
  IDefaultProps
> {
  public static defaultProps: IDefaultProps = {
    canEditQuantity: false,
  };

  public state: IState = {
    quantity: 1,
  };

  public handleClickOnAlertButton = async (): Promise<void> => {
    const { error } = await this.props
      .userProductAlertStockUpdate(this.props.product)
      .catch((err: ErrorResponse) => {
        if (
          err &&
          err.graphQLErrors &&
          err.graphQLErrors.find(
            ({ code }: { code: number | undefined }) => code === 401
          )
        ) {
          toaster.notify(translate("toaster.alertSubscriptionNoSigned"));
        }

        return { error: err };
      });

    if (!error) {
      toaster.notify(translate("toaster.alertSubscriptionRequestRegistered"));
      subscribeToAlertTracking("alerteStock");
    }
  };

  public handleProductAdd = async (): Promise<void> => {
    const { product } = this.props;
    const { quantity } = this.state;

    // if product is gift card then go to gift card page
    if (product.id === giftsDictionary.giftCard.id && product.url) {
      return history.push(product.url);
    }

    const { error } = await this.props.cartProductAdd({
      productId: product.id,
      quantity,
    });
    if (!error) {
      toaster.notify(translate("toaster.productAddedToCart"));
      pushAddToCartTracking(product.name);
    }
  };

  public render(): ReactNode {
    const {
      canBeAddedToCart,
      canEditQuantity,
      className,
      product,
      fullWidth,
      children,
      fontSize: rawFontSize = theme.fontSizes.medium,
      label,
    } = this.props;
    const { quantity } = this.state;
    const outOfStock = !!(product && product.outOfStock);
    const fontSize =
      typeof rawFontSize === "number" ? rawFontSize + "px" : rawFontSize;

    const isFullWidth = (): boolean =>
      typeof fullWidth !== "undefined" ? fullWidth : true;

    const title = label
      ? label
      : !product.presale
      ? translate("product.addToCart")
      : translate("product.preorder");

    return (
      <Wrapper className={className} fullWidth={isFullWidth()}>
        {outOfStock && canBeAddedToCart ? (
          <ActionButton
            loading={this.props.userProductAlertStockUpdateLoading}
            title={translate("product.getStockAlert")}
            onClick={this.handleClickOnAlertButton}
            fullWidth={isFullWidth()}
            className="product-add-button"
            fontSize={fontSize}
          >
            {children}
          </ActionButton>
        ) : (
          // TODO: Create a new quantityButton for desktop
          canBeAddedToCart &&
          (canEditQuantity ? (
            <StyledQuantityButton
              title={title}
              onClick={this.handleProductAdd}
              fullWidth={isFullWidth()}
              onQuantitySelect={this.updateProductQuantity}
              quantity={quantity}
              loading={this.props.cartProductAddLoading}
              className="product-add-button"
              fontSize={fontSize}
            >
              {children}
            </StyledQuantityButton>
          ) : (
            <ActionButton
              title={title}
              onClick={this.handleProductAdd}
              fullWidth={isFullWidth()}
              loading={this.props.cartProductAddLoading}
              className="product-add-button"
              fontSize={fontSize}
            >
              {children}
            </ActionButton>
          ))
        )}
      </Wrapper>
    );
  }

  public updateProductQuantity = (quantity: number): void => {
    this.setState({ quantity });
  };
}
